import { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import ParticleNetwork from '../components/landing/ParticleNetwork';
import LoginModal from '../components/landing/LoginModal';

const STATS = [
  '🛡️ 127 Emergencies Resolved',
  '🎯 98.3% Response Accuracy',
  '🏙️ 34 Cities Protected',
  '⚡ Avg Response Time: 2.4 min',
  '🌐 Mesh Network: Always On',
  '🤖 AI-Powered Verification: Active',
];

const PIPELINE_STEPS = [
  { icon: '📡', label: 'Input', desc: 'Multi-source emergency reports from citizens, IoT sensors & social media' },
  { icon: '🧠', label: 'AI Detection', desc: 'Gemini AI analyzes patterns and classifies incident type and severity' },
  { icon: '✅', label: 'Verification', desc: 'Cross-references reports to compute confidence score and filter false alarms' },
  { icon: '⚡', label: 'Decision', desc: 'Priority scoring and autonomous resource allocation in under 2 seconds' },
  { icon: '📢', label: 'Communication', desc: 'Real-time alerts to responders, citizens and command via WebSocket + FCM' },
  { icon: '📚', label: 'Learning', desc: 'Post-crisis AI analysis feeds back into the model for continuous improvement' },
];

const ROLES = [
  { key: 'citizen', label: 'Citizen', icon: '👤', color: '#00C896', desc: 'Report emergencies, access safe zones, get AI guidance' },
  { key: 'responder', label: 'Responder', icon: '🚒', color: '#FFB300', desc: 'Accept missions, navigate to incidents, coordinate teams' },
  { key: 'admin', label: 'Admin / Commander', icon: '⚡', color: '#FF3B3B', desc: 'Command center, analytics, AI predictions, simulations' },
];

export default function LandingPage() {
  const [activeRole, setActiveRole] = useState(null);
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-navy relative overflow-x-hidden">
      {/* Hero */}
      <section className="relative min-h-screen flex flex-col items-center justify-center text-center px-4 overflow-hidden">
        <ParticleNetwork />

        <div className="relative z-10 max-w-4xl mx-auto">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex items-center justify-center gap-4 mb-6"
          >
            <div className="w-16 h-16 rounded-2xl bg-electric flex items-center justify-center shadow-glow-blue">
              <span className="font-display font-black text-2xl text-navy">A</span>
            </div>
            <h1 className="font-display font-black text-4xl md:text-6xl text-electric tracking-widest glow-text">
              AEGISNET AI
            </h1>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-gray-300 text-lg md:text-xl mb-2 font-light tracking-wide"
          >
            {t('tagline')}
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-gray-500 text-sm mb-10 font-mono"
          >
            Google Solution Challenge 2026 — Rapid Crisis Response
          </motion.p>

          {/* Role buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
          >
            {ROLES.map((role) => (
              <motion.button
                key={role.key}
                whileHover={{ scale: 1.05, boxShadow: `0 0 24px ${role.color}60` }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setActiveRole(role.key)}
                className="flex items-center gap-3 px-8 py-4 rounded-xl font-display font-bold text-navy tracking-wider transition-all"
                style={{ background: role.color }}
                aria-label={`Login as ${role.label}`}
              >
                <span className="text-xl">{role.icon}</span>
                <span>{role.label}</span>
              </motion.button>
            ))}
          </motion.div>

          {/* Role descriptions */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto"
          >
            {ROLES.map((role) => (
              <div
                key={role.key}
                className="glass-card p-3 text-sm text-gray-400"
                style={{ borderColor: role.color + '30' }}
              >
                <span style={{ color: role.color }} className="font-semibold block mb-1">
                  {role.icon} {role.label}
                </span>
                {role.desc}
              </div>
            ))}
          </motion.div>
        </div>

        {/* Stats ticker */}
        <div className="absolute bottom-0 left-0 right-0 bg-navy-card border-t border-navy-border py-2 z-10">
          <div className="ticker-wrap">
            <div className="ticker">
              {[...STATS, ...STATS].map((stat, i) => (
                <span key={i} className="inline-block mx-8 text-sm font-mono text-electric">
                  {stat}
                  <span className="mx-8 text-navy-border">|</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 px-4 max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-display font-bold text-3xl text-white mb-3">
            The <span className="text-electric">6-Layer</span> Intelligence Pipeline
          </h2>
          <p className="text-gray-400">From first report to resolved incident in minutes</p>
        </motion.div>

        <div className="relative">
          {/* Connecting line */}
          <div className="hidden md:block absolute top-12 left-0 right-0 h-0.5 bg-gradient-to-r from-electric via-amber to-emerald opacity-30" />

          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            {PIPELINE_STEPS.map((step, i) => (
              <motion.div
                key={step.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glass-card p-5 text-center relative group hover:border-electric transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-electric/10 border border-electric/30 flex items-center justify-center text-2xl mx-auto mb-3 group-hover:bg-electric/20 transition-colors">
                  {step.icon}
                </div>
                <div className="text-xs font-mono text-electric mb-1">Step {i + 1}</div>
                <div className="font-bold text-white mb-2">{step.label}</div>
                <div className="text-xs text-gray-400 leading-relaxed">{step.desc}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-4 bg-navy-card border-y border-navy-border">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="font-display font-bold text-2xl text-white mb-10">
            Why <span className="text-crimson">AegisNet</span> Wins
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: '🔮', label: 'Predictive AI', desc: 'Risk forecast 2h ahead' },
              { icon: '📡', label: 'Mesh Offline', desc: 'Works without internet' },
              { icon: '🤖', label: 'Gemini-Powered', desc: 'Real-time AI analysis' },
              { icon: '🗺️', label: 'Digital Twin', desc: 'Crisis simulation engine' },
            ].map((f) => (
              <motion.div
                key={f.label}
                whileHover={{ scale: 1.03 }}
                className="glass-card p-5 text-center hover:border-electric transition-colors"
              >
                <div className="text-3xl mb-2">{f.icon}</div>
                <div className="font-bold text-white text-sm">{f.label}</div>
                <div className="text-xs text-gray-400 mt-1">{f.desc}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-center py-8 text-gray-500 text-sm">
        <p>Built by <span className="text-electric font-bold">RANS.EXE</span> · Leader: Rudra Narayan Nahak</p>
        <p className="mt-1">Google Solution Challenge 2026 — Rapid Crisis Response</p>
      </footer>

      {/* Login modal */}
      {activeRole && (
        <LoginModal role={activeRole} onClose={() => setActiveRole(null)} />
      )}
    </div>
  );
}
